/**
 * audioUploadService.js - Audio Upload & Compression Service for DAW
 * 
 * Features:
 * - Audio compression (MP3, OGG, WAV)
 * - Upload to R2 storage via API
 * - Progress tracking
 * - Database integration for tracks and projects
 */

import api from '../services/api';

// =============================================================================
// AUDIO COMPRESSION
// =============================================================================

/**
 * Compress AudioBuffer to a compressed format
 * Uses OfflineAudioContext for processing
 */
export const compressAudioBuffer = async (audioBuffer, options = {}) => {
  const {
    format = 'wav', // 'wav', 'webm', 'mp3' (mp3 requires external encoder)
    quality = 0.8,
    sampleRate = 44100,
    bitDepth = 16
  } = options;

  // Resample if needed
  let processedBuffer = audioBuffer;
  if (audioBuffer.sampleRate !== sampleRate) {
    const offlineCtx = new OfflineAudioContext(
      audioBuffer.numberOfChannels,
      audioBuffer.duration * sampleRate,
      sampleRate
    );
    const source = offlineCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineCtx.destination);
    source.start();
    processedBuffer = await offlineCtx.startRendering();
  }

  // Convert to the requested format
  switch (format) {
    case 'webm':
      return await encodeToWebM(processedBuffer, quality);
    case 'wav':
    default:
      return encodeToWav(processedBuffer, bitDepth);
  }
};

/**
 * Encode AudioBuffer to WAV format
 */
const encodeToWav = (audioBuffer, bitDepth = 16) => {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = bitDepth === 32 ? 3 : 1; // 3 = float, 1 = PCM
  const bytesPerSample = bitDepth / 8;
  
  // Interleave channels
  const length = audioBuffer.length * numChannels;
  const buffer = new ArrayBuffer(44 + length * bytesPerSample);
  const view = new DataView(buffer);
  
  // Write WAV header
  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * bytesPerSample, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // Subchunk1Size
  view.setUint16(20, format, true); // AudioFormat
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true); // ByteRate
  view.setUint16(32, numChannels * bytesPerSample, true); // BlockAlign
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, length * bytesPerSample, true);
  
  // Get channel data
  const channels = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channels.push(audioBuffer.getChannelData(ch));
  }
  
  // Write interleaved samples
  let offset = 44;
  for (let i = 0; i < audioBuffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]));
      if (bitDepth === 16) {
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      } else if (bitDepth === 32) {
        view.setFloat32(offset, sample, true);
      }
      offset += bytesPerSample;
    }
  }
  
  return new Blob([buffer], { type: 'audio/wav' });
};

/**
 * Encode AudioBuffer to WebM format using MediaRecorder
 */
const encodeToWebM = async (audioBuffer, quality) => {
  const audioContext = new AudioContext();
  const destination = audioContext.createMediaStreamDestination();
  const source = audioContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(destination);
  
  const mediaRecorder = new MediaRecorder(destination.stream, {
    mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
      ? 'audio/webm;codecs=opus' 
      : 'audio/webm',
    audioBitsPerSecond: quality * 192000
  });
  
  const chunks = [];
  
  return new Promise((resolve) => {
    mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
    mediaRecorder.onstop = () => {
      audioContext.close();
      resolve(new Blob(chunks, { type: 'audio/webm' }));
    };
    
    mediaRecorder.start();
    source.start();
    source.onended = () => {
      setTimeout(() => mediaRecorder.stop(), 100);
    };
  });
};

// =============================================================================
// UPLOAD SERVICE
// =============================================================================

/**
 * Upload audio to the server with progress tracking
 */
export const uploadAudio = async (audioBlob, metadata = {}, onProgress = null) => {
  const {
    title = `Audio ${Date.now()}`,
    description = '',
    duration = 0,
    is_public = false,
    project_id = null,
    track_id = null
  } = metadata;

  const formData = new FormData();
  
  // Determine file extension based on blob type
  let extension = 'wav';
  if (audioBlob.type.includes('webm')) extension = 'webm';
  else if (audioBlob.type.includes('mp3') || audioBlob.type.includes('mpeg')) extension = 'mp3';
  else if (audioBlob.type.includes('ogg')) extension = 'ogg';
  
  const filename = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.${extension}`;
  const file = new File([audioBlob], filename, { type: audioBlob.type });
  
  formData.append('audio', file);
  formData.append('title', title);
  formData.append('description', description);
  formData.append('duration', duration.toString());
  formData.append('is_public', is_public.toString());
  
  if (project_id) formData.append('project_id', project_id);
  if (track_id) formData.append('track_id', track_id);

  try {
    if (onProgress) onProgress({ stage: 'uploading', progress: 0 });
    
    const result = await api.saveAudioProject(file, {
      title,
      description,
      duration,
      is_public
    });
    
    if (onProgress) onProgress({ stage: 'complete', progress: 1 });
    
    return result;
  } catch (error) {
    if (onProgress) onProgress({ stage: 'error', error: error.message });
    throw error;
  }
};

/**
 * Upload audio as a project asset (for existing DAW projects)
 */
export const uploadProjectAudio = async (projectId, audioBlob, metadata = {}, onProgress = null) => {
  const {
    name = `Track ${Date.now()}`,
    asset_type = 'audio'
  } = metadata;

  const formData = new FormData();
  
  let extension = 'wav';
  if (audioBlob.type.includes('webm')) extension = 'webm';
  else if (audioBlob.type.includes('mp3')) extension = 'mp3';
  
  const filename = `${name.replace(/[^a-zA-Z0-9]/g, '_')}.${extension}`;
  const file = new File([audioBlob], filename, { type: audioBlob.type || 'audio/wav' });
  
  formData.append('file', file);
  formData.append('name', name);
  formData.append('asset_type', asset_type);

  try {
    if (onProgress) onProgress({ stage: 'uploading', progress: 0 });
    
    const result = await api.uploadProjectAsset(projectId, formData);
    
    if (onProgress) onProgress({ stage: 'complete', progress: 1, asset: result });
    
    return result;
  } catch (error) {
    if (onProgress) onProgress({ stage: 'error', error: error.message });
    throw error;
  }
};

// =============================================================================
// PROJECT MANAGEMENT
// =============================================================================

/**
 * Create a new DAW project in the database
 */
export const createDAWProject = async (projectData = {}) => {
  const {
    name = 'Untitled Project',
    description = '',
    bpm = 120,
    time_signature = '4/4',
    is_public = false
  } = projectData;

  return await api.createStudioProject({
    name,
    description,
    settings: {
      bpm,
      time_signature,
      sample_rate: 44100
    },
    is_public
  });
};

/**
 * Save DAW project state (tracks, clips, settings)
 */
export const saveDAWProjectState = async (projectId, state) => {
  const {
    tracks = [],
    bpm,
    masterVolume,
    duration
  } = state;

  // Update project settings
  await api.updateStudioProject(projectId, {
    settings: {
      bpm,
      masterVolume,
      duration
    }
  });

  // Sync tracks to database
  for (const track of tracks) {
    if (track.dbId) {
      // Update existing track
      await api.updateProjectTrack(track.dbId, {
        name: track.name,
        color: track.color,
        volume: track.volume,
        pan: track.pan,
        muted: track.muted,
        solo: track.solo,
        effects: track.effects || []
      });
    } else {
      // Create new track
      const dbTrack = await api.createProjectTrack(projectId, {
        name: track.name,
        track_type: track.type || 'audio',
        color: track.color,
        volume: track.volume,
        pan: track.pan,
        muted: track.muted,
        solo: track.solo,
        effects: track.effects || []
      });
      track.dbId = dbTrack.id;
    }
  }

  return { success: true, projectId };
};

/**
 * Load DAW project from database
 */
export const loadDAWProject = async (projectId) => {
  const project = await api.getStudioProject(projectId);
  const tracks = await api.getProjectTracks(projectId);
  const assets = await api.getProjectAssets(projectId);

  // Load clips for each track
  const tracksWithClips = await Promise.all(
    tracks.map(async (track) => {
      const clips = await api.getTrackClips(track.id);
      return { ...track, clips };
    })
  );

  return {
    project,
    tracks: tracksWithClips,
    assets
  };
};

/**
 * Export final mix and upload
 */
export const exportAndUploadMix = async (audioBuffer, projectId, metadata = {}, onProgress = null) => {
  if (onProgress) onProgress({ stage: 'compressing', progress: 0 });
  
  // Compress to WAV (or WebM for smaller size)
  const compressed = await compressAudioBuffer(audioBuffer, {
    format: metadata.format || 'wav',
    quality: metadata.quality || 0.9,
    sampleRate: 44100
  });
  
  if (onProgress) onProgress({ stage: 'compressing', progress: 0.5 });

  // Upload
  const result = await uploadAudio(compressed, {
    title: metadata.title || `Mix Export ${new Date().toISOString()}`,
    description: metadata.description || '',
    duration: audioBuffer.duration,
    is_public: metadata.is_public || false,
    project_id: projectId
  }, onProgress);

  return result;
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get audio file size estimate
 */
export const estimateFileSize = (duration, format = 'wav', sampleRate = 44100, channels = 2, bitDepth = 16) => {
  switch (format) {
    case 'wav':
      return duration * sampleRate * channels * (bitDepth / 8);
    case 'webm':
      return duration * 24000; // ~192kbps
    case 'mp3':
      return duration * 20000; // ~160kbps
    default:
      return duration * sampleRate * channels * 2;
  }
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export default {
  compressAudioBuffer,
  uploadAudio,
  uploadProjectAudio,
  createDAWProject,
  saveDAWProjectState,
  loadDAWProject,
  exportAndUploadMix,
  estimateFileSize,
  formatFileSize
};
